import React from 'react';
import { css, keyframes } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleFill, CheckmarkCircleFill, EditOutline, StarAiFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, Stack, Typography } from '@lyearn/ui';

export type GenerateVideoBannerState =
  | 'fresh_empty'
  | 'script_generating'
  | 'script_ready'
  | 'storyboard_generating'
  | 'storyboard_ready'
  | 'video_rendering'
  | 'video_ready'
  | 'video_failed';

interface GenerateVideoBannerProps {
  state: GenerateVideoBannerState;
  actionLabel?: string;
  className?: string;
  onCreateVideoClick: () => void;
}

const SKELETON_LINE_WIDTHS = [
  '62%',
  '96%',
  '88%',
  '100%',
  '74%',
  '92%',
  '100%',
  '84%',
  '97%',
  '66%',
  '100%',
  '78%',
  '94%',
  '72%',
];

const shimmerAnimation = keyframes`
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
`;

const shimmerLine = css`
  background: linear-gradient(90deg, #e4e4e4 22%, #f0f0f0 50%, #e4e4e4 78%);
  background-size: 220% 100%;
  animation: ${shimmerAnimation} 2.2s linear infinite;
`;

const verticalScrollAnimation = keyframes`
  0% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-50%);
  }
`;

const rotateClockwiseAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
`;

const verticallyScrollingLines = css`
  animation: ${verticalScrollAnimation} 14s linear infinite;
`;

const rotateClockwise = css`
  animation: ${rotateClockwiseAnimation} 1.8s linear infinite;
  transform-origin: center;
`;

const GenerateVideoBanner = (props: GenerateVideoBannerProps) => {
  const { state, actionLabel, className, onCreateVideoClick } = props;
  const { t } = useTranslation('Course');

  const isGenerating =
    state === 'script_generating' ||
    state === 'storyboard_generating' ||
    state === 'video_rendering';

  const statusText =
    state === 'fresh_empty'
      ? t('Course:AiVideoGeneration.CreateALessonVideoInMinutesWithLyearnAI')
      : state === 'video_failed'
      ? 'Failed to generate AI Video! Please try again.'
      : state === 'script_generating'
      ? t('Course:AiVideoGeneration.GeneratingAIVideoScript')
      : state === 'storyboard_generating'
      ? t('Course:AiVideoGeneration.GeneratingStoryboard')
      : state === 'storyboard_ready'
      ? t('Course:AiVideoGeneration.StoryboardIsReady')
      : t('Course:AiVideoGeneration.AIVideoScriptIsReady');

  const defaultActionText =
    state === 'fresh_empty'
      ? t('Course:AiVideoGeneration.CreateVideo')
      : state === 'video_failed'
      ? t('Course:AiVideoGeneration.ConfigureVideo')
      : state === 'storyboard_generating' || state === 'storyboard_ready'
      ? t('Course:AiVideoGeneration.ConfigureVideo')
      : t('Course:AiVideoGeneration.EditScriptAndStyle');
  const actionText = actionLabel || defaultActionText;

  return (
    <Box className={cn('relative overflow-hidden rounded-12 bg-bg-primary py-24', className)}>
      <Box
        className="relative overflow-hidden rounded-[2.8rem]"
        style={{
          borderColor: '#B7B7B7',
          backgroundColor: '#F6F6F6',
          padding: '1.8rem',
        }}>
        <Box
          className="relative overflow-hidden rounded-[2.4rem]"
          style={{
            minHeight: '31rem',
            backgroundColor: '#F6F6F6',
          }}>
          <Box className="absolute inset-0 overflow-hidden">
            <Stack className={cn('px-28 py-24', verticallyScrollingLines)} direction="col" gap={11}>
              {SKELETON_LINE_WIDTHS.concat(SKELETON_LINE_WIDTHS).map((width, index) => (
                <Stack
                  key={index}
                  className={`h-[0.8rem] rounded-full ${shimmerLine}`}
                  style={{
                    width,
                    animationDelay: `${(index % SKELETON_LINE_WIDTHS.length) * 0.07}s`,
                    opacity: index % 2 ? 0.78 : 0.96,
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(246,246,246,0.96) 0%, rgba(246,246,246,0.84) 44%, rgba(246,246,246,0.3) 70%, rgba(246,246,246,0) 100%)',
            }}
          />

          <Stack
            alignItems="center"
            className="relative z-10 min-h-[31rem] px-24 py-20"
            direction="col"
            justifyContent="center">
            {state === 'video_failed' ? (
              <AlertTriangleFill
                className="text-icon-error"
                style={{ height: '4.4rem', width: '4.4rem' }}
              />
            ) : isGenerating ? (
              <StarAiFill
                className={cn('text-[#5A56E9]', rotateClockwise)}
                style={{ height: '4.4rem', width: '4.4rem' }}
              />
            ) : (
              <CheckmarkCircleFill
                fillColorToken="icon-success"
                style={{ height: '4.4rem', width: '4.4rem' }}
              />
            )}

            <Stack
              alignItems="center"
              className="mt-24 w-full"
              direction="row"
              justifyContent="between">
              <Stack
                className="h-[0.6rem] w-[0.6rem] rounded-full"
                style={{ backgroundColor: '#B4B4B4' }}
              />
              <Typography className="text-center" color="text-text-primary" variant="label-01">
                {statusText}
              </Typography>
              <Stack
                className="h-[0.6rem] w-[0.6rem] rounded-full"
                style={{ backgroundColor: '#B4B4B4' }}
              />
            </Stack>

            <Button
              StartIcon={EditOutline}
              appearance="secondary"
              className="mt-22 rounded-[1.6rem] border-[#D7D7D7] bg-bg-primary px-24"
              shape="square"
              size="small"
              variant="outlined"
              onClick={onCreateVideoClick}>
              {actionText}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default GenerateVideoBanner;
